/**
 * One Games, and the sports inside it.
 *
 * A programme is an ordinary competition once it exists — it carries its sport,
 * everything below inherits it, and it is edited through that sport's own tree.
 * This page only exists for the part that has no sport: the Games itself, and
 * putting a sport into it.
 */

import { Link, useParams } from "react-router-dom";

import { AddProgramme, type Addable } from "@/components/events/AddProgramme";
import { EventWorkflows } from "@/components/events/EventWorkflows";
import { EventIntegrations } from "@/components/integrations/EventIntegrations";
import {
  Card,
  Crumbs,
  Empty,
  LinkButton,
  PageHead,
  Pill,
  Problem,
  Section,
  Table,
  Td,
  Th,
} from "@/components/kit";
import { ApiError, getEvent, listDefinitions } from "@/lib/api/client";
import { displayName } from "@/lib/format";
import { useApi } from "@/spa/useApi";

export function EventDetailPage() {
  const { code = "" } = useParams<{ code: string }>();

  const { data, loading, error, cause } = useApi(
    () => Promise.all([getEvent(code), listDefinitions()]),
    [code],
    "failed to load",
  );

  if (error) {
    if (cause instanceof ApiError && cause.status === 404) {
      return <Problem>No event coded {code}.</Problem>;
    }
    return (
      <Problem>
        Could not load {code}: {error}
      </Problem>
    );
  }
  if (!data) return <PageHead title="Event" note={loading ? "Loading…" : ""} />;

  const [event, sports] = data;

  const inside = new Set(event.programmes.map((p) => p.sportCode));
  // A grouping sport declares no top level, because nothing is entered into it
  // — so it cannot hold a programme, and its disciplines stand in for it.
  const seen = new Set<string>();
  const available: Addable[] = sports
    .filter((row) => {
      if (inside.has(row.sportCode) || seen.has(row.sportCode)) return false;
      seen.add(row.sportCode);
      return (row.topLevels ?? []).length > 0;
    })
    .map((row) => ({
      sportCode: row.sportCode,
      partOf: row.partOf,
      levels: row.topLevels ?? [],
    }));
  const when =
    event.startDate && event.endDate
      ? `${new Date(event.startDate).toLocaleDateString()} – ${new Date(event.endDate).toLocaleDateString()}`
      : "Dates not set";

  return (
    <>
      <Crumbs trail={[{ label: "Events", href: "/events" }, { label: event.name }]} />
      <PageHead
        title={event.name}
        note={`${when} · ${event.sports} sport${event.sports === 1 ? "" : "s"} · ${event.fixtures} fixture${event.fixtures === 1 ? "" : "s"}`}
        actions={
          <span style={{ display: "flex", gap: "var(--s2)", alignItems: "center" }}>
            <LinkButton to={`/events/${encodeURIComponent(event.code)}/console`} tone="primary">
              Console
            </LinkButton>
            <AddProgramme code={event.code} sports={available} />
          </span>
        }
      />

      <Section
        title="Sports"
        note="Each one holds its own competitions, and everything below inherits it."
      >
        {event.programmes.length === 0 ? (
          <Card>
            <Empty
              title="No sports yet"
              note="Add the sports taking part. Each becomes an ordinary competition inside this event, edited through that sport's own tree."
              action={<AddProgramme code={event.code} sports={available} />}
            />
          </Card>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Sport</Th>
                <Th>Programme</Th>
                <Th num>Fixtures</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {event.programmes.map((programme) => (
                <tr key={programme.id}>
                  <Td strong>{displayName(programme.sportCode)}</Td>
                  <Td quiet>{programme.name}</Td>
                  <Td num>
                    {programme.fixtures === 0 ? (
                      <Pill tone="warn">none yet</Pill>
                    ) : (
                      programme.fixtures
                    )}
                  </Td>
                  <Td>
                    <Link
                      to={`/${programme.sportCode}/competitions`}
                      style={{ fontSize: "var(--t-sm)" }}
                    >
                      Open its tree
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <EventWorkflows code={event.code} sports={event.programmes.map((p) => p.sportCode)} />

      <EventIntegrations code={event.code} />
    </>
  );
}
